import { Component, forwardRef, inject, input, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
  ControlValueAccessor,
  ReactiveFormsModule,
  FormControl,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  FormsModule,
  FormArray,
  ValidationErrors,
  Validator
} from '@angular/forms';
import { NgClass, TitleCasePipe } from '@angular/common';
import { Subscription } from 'rxjs';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { NgOptionComponent, NgSelectComponent } from '@ng-select/ng-select';

export interface PhoneNumber {
  id?: string;
  phoneNumber?: string;
  type?: string;
}

@Component({
  selector: 'phone-number-field',
  templateUrl: './phone-number-field.component.html',
  standalone: true,
  imports: [TitleCasePipe, ReactiveFormsModule, FormsModule, NgbTooltip, NgClass, NgSelectComponent, NgOptionComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneNumberFieldComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PhoneNumberFieldComponent),
      multi: true
    }
  ]
})
export class PhoneNumberFieldComponent implements OnInit, ControlValueAccessor, Validator {
  private fb = inject(FormBuilder);
  required = input<boolean>(false);

  phoneTypes: string[] = ['mobile', 'home', 'office', 'fax'];
  phoneArray: FormArray = this.fb.array([]);
  propagateChange: any = () => {};
  private onValidatorChange: () => void = () => {};
  private formValueSub: Subscription | null = null;

  ngOnInit(): void {
    // Initialize with mobile phone by default
    this.addPhoneField('mobile');
    this.subscribeChanges();
    this.onValidatorChange();
  }

  writeValue(value: PhoneNumber[] | null): void {
    this.phoneArray.clear();

    if (value && Array.isArray(value)) {
      // Add existing phone numbers
      value.forEach(phone => {
        if (phone) {
          this.addPhoneFieldWithData(phone);
        }
      });
    }

    // Ensure at least mobile exists
    if (!this.hasPhoneType('mobile')) {
      this.addPhoneField('mobile');
    }

    this.propagateChange(this.phoneArray.value);
    this.subscribeChanges();
    this.onValidatorChange();
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
    this.subscribeChanges();
  }

  registerOnTouched(fn: any): void {}

  validate(_control: AbstractControl): ValidationErrors | null {
    return this.phoneArray.invalid ? { phoneNumberInvalid: true } : null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.phoneArray.disable() : this.phoneArray.enable();
  }

  addPhoneField(type?: string): void {
    const availableType = type || this.phoneTypes.find(t => !this.hasPhoneType(t));
    if (!availableType) return;

    const phoneGroup = this.createPhoneFormGroup({
      type: availableType,
      phoneNumber: ''
    });

    this.phoneArray.push(phoneGroup);
  }

  addPhoneFieldWithData(phone: PhoneNumber): void {
    const phoneGroup = this.createPhoneFormGroup(phone);
    this.phoneArray.push(phoneGroup);
  }

  removePhoneField(index: number): void {
    const phoneGroup = this.phoneArray.at(index) as FormGroup;
    const phoneType = phoneGroup.get('type')?.value;

    // Don't allow removing mobile if it's the only one
    if (phoneType === 'mobile' && this.phoneArray.length === 1) {
      return;
    }
    this.phoneArray.removeAt(index);
  }

  onTypeChange(index: number, newType: string | Event): void {
    // Extract value if it's an Event object
    const typeValue = typeof newType === 'string' ? newType : (newType.target as HTMLSelectElement).value;

    const phoneGroup = this.phoneArray.at(index) as FormGroup;
    const currentType = phoneGroup.get('type')?.value;

    if (currentType === typeValue || this.hasPhoneType(typeValue)) {
      return;
    }

    phoneGroup.patchValue({ type: typeValue });
  }
  get selectedTypes(): string[] {
    return this.phoneArray.controls.map(control => (control as FormGroup).get('type')?.value).filter(type => type);
  }

  get visiblePhones(): FormGroup[] {
    return this.phoneArray.controls
      .filter(control => !(control as FormGroup).get('isDeleted')?.value)
      .map(control => control as FormGroup);
  }

  getFormControl(index: number, controlName: string): FormControl {
    const phoneGroup = this.visiblePhones[index];
    return phoneGroup.get(controlName) as FormControl;
  }

  getPhoneGroup(index: number): FormGroup {
    return this.visiblePhones[index];
  }

  canAddMore(): boolean {
    const visibleTypes = this.visiblePhones.map(group => group.get('type')?.value);
    return visibleTypes.length < this.phoneTypes.length;
  }

  getAvailableTypes(currentIndex: number): string[] {
    const currentType = this.visiblePhones[currentIndex]?.get('type')?.value;
    const usedTypes = this.visiblePhones
      .map((group, idx) => (idx !== currentIndex ? group.get('type')?.value : null))
      .filter(type => type);

    return this.phoneTypes.filter(type => type === currentType || !usedTypes.includes(type));
  }

  private createPhoneFormGroup(phone: PhoneNumber): FormGroup {
    const validators = [];
    if (this.required()) {
      validators.push(Validators.required);
    }

    return this.fb.group({
      id: [phone.id || null],
      phoneNumber: [phone.phoneNumber || '', validators],
      type: [phone.type || 'mobile']
    });
  }

  private hasPhoneType(type: string): boolean {
    return this.visiblePhones.some(group => group.get('type')?.value === type);
  }

  private subscribeChanges(): void {
    this.formValueSub?.unsubscribe();
    this.formValueSub = this.phoneArray.valueChanges.subscribe(val => {
      this.propagateChange(val);
    });
  }
}
